import React from 'react'
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  Dimensions,
} from 'react-native'
import { Home, Search, PlusSquare, Heart, User } from 'lucide-react-native'
import { useAuth } from '../contexts/AuthContext'
import { useNavigation } from '@react-navigation/native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RootStackParamList } from '../types/navigation'
import { useNavShell } from '../contexts/NavShellContext'
import { useLanguage } from '@fliisi/i18n'
import theme from '../theme'

const { width } = Dimensions.get('window')
const isDesktop = width > 768

type NavItem = {
  key: string
  icon: React.ComponentType<{ size: number; color: string; strokeWidth?: number }>
  label: string
  route: keyof RootStackParamList
}

// Routes grouped under each tab for active-state highlighting
const routeGroups: Record<string, string[]> = {
  home: ['BuyerHome', 'ProductView'],
  search: ['SearchScreen', 'MapBrowse', 'StoreBrowse'],
  sell: ['JoinMarket', 'CreateMarket', 'CreateStore', 'Home', 'Camera', 'StoreCamera', 'DraftProducts', 'ProductDetail', 'BulkUpload',
    'MarketAdmin', 'MarketSellers', 'MarketSpaces', 'SpaceReservations', 'SpaceBrowse',
    'MyStores', 'StoreDetail', 'StoreInventory', 'StoreSettings'],
  favorites: ['Favorites'],
  profile: [
    'BuyerProfile', 'ProfileSettings', 'MyStores', 'StoreDetail', 'StoreInventory',
    'StoreSettings', 'CreateStore', 'MerchantStoreSelect', 'MerchantDashboard',
    'MerchantPOS', 'MerchantActivity', 'MerchantMore', 'MerchantStaff',
  ],
}

export default function BottomNav() {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>()
  const { routeName } = useNavShell()
  const { session } = useAuth()
  const { t } = useLanguage()

  const navItems: NavItem[] = [
    { key: 'home',      icon: Home,       label: t('home'),      route: 'BuyerHome' },
    { key: 'search',    icon: Search,     label: t('search'),    route: 'SearchScreen' },
    { key: 'sell',      icon: PlusSquare, label: t('sell'),      route: 'JoinMarket' },
    { key: 'favorites', icon: Heart,      label: t('favorites'), route: 'Favorites' },
    { key: 'profile',   icon: User,       label: t('profile'),   route: 'BuyerProfile' },
  ]

  const isActive = (item: NavItem) => {
    const group = routeGroups[item.key] || [item.route]
    return group.includes(routeName)
  }

  const handlePress = (item: NavItem) => {
    // Myy and Suosikit require auth
    if ((item.key === 'sell' || item.key === 'favorites') && !session) {
      navigation.navigate('Login')
      return
    }
    navigation.navigate(item.route as any)
  }

  return (
    <View style={[styles.container, isDesktop && styles.containerDesktop]}>
      <View style={[styles.nav, isDesktop && styles.navDesktop]}>
        {navItems.map((item) => {
          const active = isActive(item)
          const IconComponent = item.icon
          const isSell = item.key === 'sell'
          const iconColor = isSell
            ? (active ? theme.colors.brand.secondary : theme.colors.brand.secondary)
            : (active ? theme.colors.brand.primary : theme.colors.text.secondary)
          return (
            <TouchableOpacity
              key={item.key}
              style={styles.navItem}
              onPress={() => handlePress(item)}
            >
              <IconComponent
                size={22}
                color={iconColor}
                strokeWidth={active ? 2.5 : 1.8}
              />
              <Text style={[
                styles.navLabel,
                active && styles.navLabelActive,
                isSell && styles.navLabelSell,
              ]}>
                {item.label}
              </Text>
            </TouchableOpacity>
          )
        })}
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.colors.background.card,
    borderTopWidth: 1,
    borderTopColor: theme.colors.ui.border,
    paddingBottom: Platform.OS === 'ios' ? 20 : 0,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 -2px 8px rgba(62, 66, 58, 0.08)' }
      : {
          shadowColor: theme.colors.text.primary,
          shadowOffset: { width: 0, height: -2 },
          shadowOpacity: 0.1,
          shadowRadius: 8,
          elevation: 8,
        }),
  },
  containerDesktop: {
    borderTopWidth: 0,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.border,
    paddingBottom: 0,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 8px rgba(62, 66, 58, 0.08)' }
      : { shadowOffset: { width: 0, height: 2 } }),
  },
  nav: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    paddingVertical: 8,
  },
  navDesktop: {
    justifyContent: 'center',
    gap: 32,
    maxWidth: 768,
    marginHorizontal: 'auto',
  },
  navItem: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 4,
  },
  navLabel: {
    fontSize: 10,
    color: theme.colors.text.secondary,
    fontWeight: '500',
    marginTop: 3,
  },
  navLabelActive: {
    color: theme.colors.brand.primary,
    fontWeight: '700',
  },
  navLabelSell: {
    color: theme.colors.brand.secondary,
  },
})
